// src/components/PreparePanel.tsx
import React, { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { API_BASE } from "../config";
import { useAuth } from "../auth/AuthProvider";
import "./PreparePanel.css";

type ClientNote = {
  title: string;
  content: string;
  created_at: string;
};

type Props = {
  patient: string;
};

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 40; // 40 * 3s = 2 min timeout

export default function PreparePanel({ patient }: Props) {
  const { t } = useTranslation();
  const { ensureToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [preparation, setPreparation] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasNewSessions, setHasNewSessions] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);
  const [clientNotes, setClientNotes] = useState<ClientNote[]>([]);

  // Auto-generate ref to prevent double-triggering
  const autoGenerateTriggeredRef = useRef(false);
  // Polling cancel ref
  const pollCancelRef = useRef(false);

  // Poll GET until done/error/timeout
  const pollForResult = useCallback(async (patientId: string | undefined) => {
    const url = patientId
      ? `${API_BASE}/sessions/prepare?patient_id=${encodeURIComponent(patientId)}`
      : `${API_BASE}/sessions/prepare`;

    for (let i = 0; i < MAX_POLLS; i++) {
      if (pollCancelRef.current) return;

      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      if (pollCancelRef.current) return;

      try {
        const token = await ensureToken();
        const res = await fetch(url, {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const data = await res.json();

        if (data.status === "error") {
          setError(data.error || t("prepare.generationFailed"));
          setLoading(false);
          setAutoGenerating(false);
          return;
        }

        if (data.preparation && data.status !== "running") {
          setPreparation(data.preparation);
          setClientNotes(data.client_notes || []);
          setHasNewSessions(false);
          setLoading(false);
          setAutoGenerating(false);
          return;
        }
        // status === "running" — keep polling
      } catch {
        // Network error — keep polling
      }
    }

    // Timeout
    setError(t("prepare.generationTimeout"));
    setLoading(false);
    setAutoGenerating(false);
  }, [ensureToken]);

  // Reset auto-generate flag when patient changes
  useEffect(() => {
    autoGenerateTriggeredRef.current = false;
    setPreparation(null);
    setClientNotes([]);
    setError(null);
    setInitialLoading(true);
  }, [patient]);

  // Load cached preparation on mount and check for new sessions
  useEffect(() => {
    if (!patient) {
      setInitialLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const token = await ensureToken();
        const url = `${API_BASE}/sessions/prepare?patient_id=${encodeURIComponent(patient)}`;
        const res = await fetch(url, {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (cancelled) return;
        const data = await res.json();

        if (data.status === "running") {
          // Worker is already running — start polling
          setLoading(true);
          setAutoGenerating(true);
          pollForResult(patient);
        } else if (data.preparation) {
          setPreparation(data.preparation);
          setClientNotes(data.client_notes || []);
          setHasNewSessions(data.has_new_sessions ?? false);
        } else {
          setHasNewSessions(false);
        }
      } catch {
        // Ignore errors on initial load
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ensureToken, patient, pollForResult]);

  const handleGenerate = useCallback(async (force: boolean = false, isAuto: boolean = false) => {
    setLoading(true);
    setError(null);
    pollCancelRef.current = false;
    if (isAuto) setAutoGenerating(true);

    try {
      const token = await ensureToken();
      const res = await fetch(`${API_BASE}/sessions/prepare`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          patient_id: patient,
          session_count: 3,
          force: force,
        }),
      });

      const data = await res.json();

      // 202 = worker started, poll for result
      if (res.status === 202) {
        pollForResult(patient);
        return;
      }

      // 200 = cached result returned directly
      if (res.ok && data.preparation) {
        setPreparation(data.preparation);
        setClientNotes(data.client_notes || []);
        setHasNewSessions(false);
        setLoading(false);
        setAutoGenerating(false);
        return;
      }

      if (!res.ok) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }

      setPreparation(data.preparation || t("prepare.noPreparation"));
      setHasNewSessions(false);
      setLoading(false);
      setAutoGenerating(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setLoading(false);
      setAutoGenerating(false);
    }
  }, [ensureToken, patient, pollForResult]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => { pollCancelRef.current = true; };
  }, []);

  // Auto-generate when no preparation exists OR when new sessions are detected
  useEffect(() => {
    if (!patient) return;
    if (initialLoading || loading) return;
    if (autoGenerateTriggeredRef.current) return;

    const shouldGenerate = !preparation || hasNewSessions;
    if (!shouldGenerate) return;

    autoGenerateTriggeredRef.current = true;
    handleGenerate(hasNewSessions, true);
  }, [patient, preparation, hasNewSessions, loading, initialLoading, handleGenerate]);

  return (
    <div className="prepare-panel">

      {initialLoading ? (
        <div className="prepare-loading">{t("common.loading")}</div>
      ) : (autoGenerating || (loading && !preparation)) ? (
        <div className="prepare-auto-status">
          {t("prepare.loading")}
        </div>
      ) : null}

      {error && (
        <div className="prepare-error">
          {t("common.error")}: {error}
        </div>
      )}

      {clientNotes.length > 0 && (
        <div className="prepare-client-notes">
          <h2 className="prepare-client-notes-title">{t("prepare.clientNotes")}</h2>
          {clientNotes.map((note, i) => (
            <div key={i} className="prepare-client-note-card">
              <span className="prepare-client-note-date">
                {note.created_at ? note.created_at.slice(0, 10) : ""}
              </span>
              {note.title && (
                <span className="prepare-client-note-title">{note.title}</span>
              )}
              <p className="prepare-client-note-content">{note.content}</p>
            </div>
          ))}
        </div>
      )}

      {preparation && (
        <div className="prepare-result">
          <div className="prepare-result-content">
            {preparation.split("\n").map((line, i, arr) => {
              if (line.startsWith("### ")) {
                return <h3 key={i}>{line.replace("### ", "")}</h3>;
              }
              if (line.startsWith("## ")) {
                return <h2 key={i}>{line.replace("## ", "")}</h2>;
              }
              if (line.startsWith("- ")) {
                return <li key={i}>{line.replace("- ", "")}</li>;
              }
              if (line.trim() === "") {
                // Skip consecutive empty lines and empty lines after headings
                const prevLine = arr[i - 1]?.trim() || "";
                if (prevLine === "" || prevLine.startsWith("#")) {
                  return null;
                }
                return <br key={i} />;
              }
              return <p key={i}>{line}</p>;
            })}
          </div>
        </div>
      )}

    </div>
  );
}
